// ══════════════════════════════════════════════════════════════
// COMPASS – Client Invitation Effects
// ══════════════════════════════════════════════════════════════
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { ClientInvitationActions } from './client-invitation.actions';
import { ProcessActions } from '../process/process.actions';
import { ToastActions } from '../toast/toast.actions';
import { ApiConfiguration } from '../../api/api-configuration';
import { invite } from '../../api/fn/invitations/invite';
import { errorMessage, readApiError } from '../../utils/api-error.utils';
import { ApiError } from '../../models/api-error.model';

/** US-014: the invite reached an existing staff account, which can't become the client. About the email. */
const NOT_A_CLIENT_ACCOUNT = 'error.invitation.not-a-client-account';

/** The message for the email field, when the refusal is about the email. */
function emailError(apiError: ApiError): string | undefined {
  const field = apiError.fieldErrors?.['inviteEmail']
    ?? apiError.violations?.find((violation) => violation.field === 'inviteEmail')?.message;
  if (field) return field;
  return apiError.code === NOT_A_CLIENT_ACCOUNT ? apiError.message : undefined;
}

@Injectable()
export class ClientInvitationEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiConfiguration);

  readonly inviteClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientInvitationActions.inviteClient),
      switchMap(({ invitation }) => {
        const kycProcessId = invitation.kycProcessId;
        return invite(this.http, this.apiConfig.rootUrl, { body: invitation }).pipe(
          switchMap(() => kycProcessId
            // US-027: into an existing process - reload that process, not the dashboard.
            ? [
                ClientInvitationActions.inviteClientSuccess({ kycProcessId }),
                ProcessActions.refreshProcessStatus({ processId: kycProcessId }),
                ToastActions.showToast({ message: 'PROCESS.INVITE_CLIENT.SUCCESS', notificationType: 'success' }),
              ]
            : [
                ClientInvitationActions.inviteClientSuccess({}),
                ProcessActions.loadProcesses({ managementCompanyId: invitation.managementCompanyId }),
                ProcessActions.loadProcessStatistics({ managementCompanyId: invitation.managementCompanyId }),
                ToastActions.showToast({
                  message: 'CLIENT.INVITATION.SUCCESS',
                  notificationType: 'success',
                }),
              ]),
          catchError((error) => {
            const apiError = readApiError(error);
            return [
              ClientInvitationActions.inviteClientFailure({
                error: errorMessage(apiError, kycProcessId ? 'PROCESS.INVITE_CLIENT.FAILED' : 'CLIENT.INVITATION.FAILED'),
                kycProcessId,
                status: apiError.status,
                code: apiError.code,
                emailError: emailError(apiError),
              }),
            ];
          })
        );
      })
    )
  );

  /** An invite into a process is answered in its dialog and by the process page, not with this toast. */
  readonly handleFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientInvitationActions.inviteClientFailure),
      filter(({ kycProcessId }) => !kycProcessId),
      map(({ error }) =>
        ToastActions.showToast({
          message: error,
          notificationType: 'error',
        })
      )
    )
  );
}
